import { format } from "date-fns";
import moment from "moment";
import intl from "react-intl-universal";
import { downloadCsv, formatFilterAmount, formatFilterDate, parseAmountInput } from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  estimateExtractTotal,
  flattenTransactionDayGroups,
  regroupTransactionsByDay,
} from "../Extract/extractFilters";

export { estimateExtractTotal, flattenTransactionDayGroups, regroupTransactionsByDay };

export const EMPTY_RECEIPTS_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  traceId: "",
  externalId: "",
  searchQuery: "",
};

export const filtersFromReceiptsFilterObj = (filterObj = {}) => ({
  dateFrom: filterObj.start_date
    ? new Date(filterObj.start_date.replace(/-/g, "/").replace(/T.+/, ""))
    : null,
  dateTo: filterObj.end_date
    ? new Date(filterObj.end_date.replace(/-/g, "/").replace(/T.+/, ""))
    : null,
  amountMin: filterObj.start_amount
    ? String(Number(filterObj.start_amount) / 100).replace(".", ",")
    : "",
  amountMax: filterObj.end_amount
    ? String(Number(filterObj.end_amount) / 100).replace(".", ",")
    : "",
  traceId: filterObj.trace_id || "",
  externalId: filterObj.external_id || "",
  searchQuery: "",
});

export const receiptsFilterObjFromFilters = (filters) => ({
  start_date: filters.dateFrom ? format(filters.dateFrom, "yyyy-MM-dd") : "",
  end_date: filters.dateTo ? format(filters.dateTo, "yyyy-MM-dd") : "",
  start_amount: (() => {
    const parsed = parseAmountInput(filters.amountMin);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  end_amount: (() => {
    const parsed = parseAmountInput(filters.amountMax);
    return parsed === null ? "" : String(Math.round(parsed * 100));
  })(),
  trace_id: filters.traceId?.trim() || "",
  external_id: filters.externalId?.trim() || "",
});

export const hasActiveReceiptsFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.traceId !== ""
  || filters.externalId !== ""
  || Boolean(filters.searchQuery?.trim());

export const isReceiptsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "trace":
      return filters.traceId !== "";
    case "external":
      return filters.externalId !== "";
    case "more":
      return filters.traceId !== "" || filters.externalId !== "";
    default:
      return false;
  }
};

export const buildReceiptsFilterTags = (filters) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.traceId) {
    tags.push({
      key: "traceId",
      iconType: "trace",
      value: filters.traceId,
    });
  }

  if (filters.externalId) {
    tags.push({
      key: "externalId",
      iconType: "external",
      value: filters.externalId,
    });
  }

  return tags;
};

export const applyReceiptsClientFilters = (items, filters) => {
  if (!filters) return items;

  const query = filters.searchQuery?.trim().toLowerCase() || "";
  const traceId = filters.traceId?.trim().toLowerCase() || "";
  const externalId = filters.externalId?.trim().toLowerCase() || "";
  const amountMin = filters.amountMin !== "" ? Number(String(filters.amountMin).replace(",", ".")) : null;
  const amountMax = filters.amountMax !== "" ? Number(String(filters.amountMax).replace(",", ".")) : null;
  const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? new Date(filters.dateTo) : null;
  if (dateFrom) dateFrom.setHours(0, 0, 0, 0);
  if (dateTo) dateTo.setHours(23, 59, 59, 999);

  return items.filter((item) => {
    const created = item.created_at ? new Date(item.created_at) : null;
    if (dateFrom && created && created < dateFrom) return false;
    if (dateTo && created && created > dateTo) return false;

    const amountAbs = Math.abs(Number(item.amount) || 0) / 100;
    if (amountMin !== null && Number.isFinite(amountMin) && amountAbs < amountMin) return false;
    if (amountMax !== null && Number.isFinite(amountMax) && amountAbs > amountMax) return false;

    const haystack = [
      item.description,
      item.name,
      item.operation_id,
      item.trace_id,
      item.end_to_end_id,
      item.endToEndId,
      item.external_id,
      item.externalId,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (query && !haystack.includes(query)) return false;
    if (traceId && !haystack.includes(traceId)) return false;
    if (externalId && !haystack.includes(externalId)) return false;

    return true;
  });
};

export const removeReceiptsFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "traceId":
      return { ...filters, traceId: "" };
    case "externalId":
      return { ...filters, externalId: "" };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const exportReceiptsCsv = (items) => {
  const headers = [
    intl.get("grid.date"),
    intl.get("grid.entry"),
    intl.get("grid.value"),
    intl.get("receipts.filterTraceId"),
    intl.get("receipts.filterExternalId"),
  ];

  const rows = items.map((item) => {
    const amount = (Number(item.amount) || 0) / 100;
    return [
      moment(item.created_at).format("DD/MM/YYYY HH:mm"),
      item.description || "",
      amount.toFixed(2).replace(".", ","),
      item.trace_id || item.end_to_end_id || item.endToEndId || "",
      item.external_id || item.externalId || item.operation_id || "",
    ];
  });

  downloadCsv(headers, rows, `comprovantes-${format(new Date(), "yyyy-MM-dd")}.csv`);
};
